/**
 * El descuento del SOCIO, en un solo sitio.
 *
 * Vivía dentro de `Dispenser.jsx`, así que la Sala (ClubEntradas) no podía usarlo
 * y calculaba sus totales sin él: un socio con 50% veía «Total 2,50 €» sobre un
 * producto de 2,50 € y se le cobraban 1,25 € al servir. Reportado por el club el
 * 01-09-2026 con captura — el camarero canta un precio que no es el que se cobra.
 *
 * Es el «un dato, un render» de las reglas del repo: la tasa del socio se resuelve
 * AQUÍ y la usan todas las pantallas. El backend la aplica al cobrar (encadenada
 * con el descuento manual desde el #564); esto es lo que se PINTA, y tiene que
 * coincidir.
 */

/** 20% terapéutico: aplica en el Dispensario y en las COMANDAS de Sala/Barra
 * (su cobro va sin cart_type — decidido 03-09-2026: la pantalla dice lo que se
 * cobra). En la pestaña Productos del dispensario, solo el % del perfil. */
// spec: saldo-pagos-caja#lineas-de-coste
// «Terapéuticos en comandas de Sala/Barra: la pantalla dice lo que se cobra» — el
// cobro aplica el 20% terapéutico + el % de ficha, y la Sala pinta esa misma tasa.
export const THERAPEUTIC_MEMBER_DISCOUNT_RATE = 0.2;
export const THERAPEUTIC_MEMBER_DISCOUNT_LABEL = '20%';

/** «15%» → 0.15 · «20% + 10%» → 0.30 (suma con tope) · «Sin descuento»/vacío → 0. */
// spec: saldo-pagos-caja#descuentos-y-total
// «La tasa del socio sí se suma cuando su etiqueta es compuesta: "20% + 10%" es
// 30%, con tope al 100%» — ni el primer número solo ni encadenarlos.
export function parseMemberDiscount(discountStr) {
  if (!discountStr || discountStr === 'Sin descuento') return 0;
  // El backend etiqueta el terapéutico con extra como «20% + 10%» y los SUMA
  // con tope al 100% (igual que resolveMemberDiscount aquí abajo): 30%. La
  // verificación del 04-09 cazó que ni leer un solo número (20%) ni encadenarlos
  // (28%) era la tasa real del cobro.
  const pcts = [...String(discountStr).matchAll(/(\d+)%/g)].map((m) => Number(m[1]) / 100);
  if (!pcts.length) return 0;
  return Math.min(1, pcts.reduce((a, b) => a + b, 0));
}

export function memberDiscountAppliesInMode(profile, isProductosMode) {
  if (!profile) return false;
  if (!isProductosMode) return true;
  return !profile.consumption_type?.terap;
}

/**
 * Terapéuticos: Dispensario = 20% + select; Productos = solo select.
 * Lúdicos: el % del perfil.
 *
 * @param {object} profile perfil del socio
 * @param {boolean} isProductosMode true en Productos y en la Sala del club social
 * @returns {{rate:number, label:string|null}} tanto por uno y etiqueta para pintar
 */
export function resolveMemberDiscount(profile, isProductosMode) {
  if (!profile) return { rate: 0, label: null };
  const extraRate = parseMemberDiscount(profile.discount);
  const isTerap = Boolean(profile.consumption_type?.terap);

  if (isTerap) {
    if (isProductosMode) {
      if (extraRate <= 0) return { rate: 0, label: null };
      return { rate: extraRate, label: profile.discount };
    }
    const totalRate = Math.min(1, THERAPEUTIC_MEMBER_DISCOUNT_RATE + extraRate);
    if (totalRate <= 0) return { rate: 0, label: null };
    const label =
      extraRate > 0
        ? `${Math.round(THERAPEUTIC_MEMBER_DISCOUNT_RATE * 100)}% + ${profile.discount}`
        : THERAPEUTIC_MEMBER_DISCOUNT_LABEL;
    return { rate: totalRate, label };
  }

  if (!memberDiscountAppliesInMode(profile, isProductosMode)) return { rate: 0, label: null };
  if (extraRate <= 0) return { rate: 0, label: null };
  return { rate: extraRate, label: profile.discount };
}
